import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import { type ApiError, addResourceNameToQueryResult, useLegacyAxiosInstance } from './utils';

const ExternalMailboxDataSchema = z.object({
  id: z.string(),
  attributes: z.object({
    host: z.string().nullable(),
    port: z.number().nullable(),
    ssl: z.boolean().nullable(),
    login: z.string().nullable(),
    is_microsoft: z.boolean().nullable(),
    signature: z.string().nullable(),
    reauth_uri: z.string().nullable(),
  }),
});

const ExternalMailboxMetadataSchema = z.object({
  pagination: z.object({
    current_page: z.number(),
    last_page: z.number(),
    page_limit: z.number(),
    total_objects: z.number(),
  }),
});

const ExternalMailboxConfigurationSchema = z.object({
  data: z.array(ExternalMailboxDataSchema),
  metadata: ExternalMailboxMetadataSchema,
});

export type ExternalMailboxes = z.infer<typeof ExternalMailboxConfigurationSchema>;

export async function loadExternalMailboxesFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;

  const { data } = await instance.get('/external_mailbox_configurations');

  return ExternalMailboxConfigurationSchema.parse(data);
}

const emptyExternalMailboxes: ExternalMailboxes = {
  data: [],
  metadata: {
    pagination: { current_page: 1, last_page: 1, page_limit: 100, total_objects: 0 },
  },
};

export const useLoadExternalMailboxes = () => {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<ExternalMailboxes, ApiError>({
    queryKey: ['externalMailboxes'],
    queryFn: () => loadExternalMailboxesFn(axiosInstance),
    placeholderData: emptyExternalMailboxes,
  });

  return addResourceNameToQueryResult<
    ExternalMailboxes,
    ApiError,
    'externalMailboxes',
    'PreventUndefined'
  >('externalMailboxes', queryResult, emptyExternalMailboxes);
};
